import { useMutation, useQuery } from '@tanstack/react-query';
import { ToolTestSourceBodyFormatEnum } from '@reach/shared-types/tool-test-result';
import { Loader2, Play } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { partnerApi, type HttpToolDefinition, type PartnerUser } from '../../lib/api';
import { CopyButton } from './CopyButton';
import { RESPONSE_MAPPING_COPY } from './response-mapping-copy';
import {
  CONTEXT_SOURCES,
  TENANT_EXTERNAL_ID_TOKEN,
  PARTICIPANT_PHONE_TOKEN,
  CONTACT_EXTERNAL_ID_TOKEN,
  VOICE_CALL_ID_TOKEN,
  buildArgsExample,
  referencesContextSource,
} from './tool-inputs';
import { normalizeToolScope } from './tool-scopes';
import { tenantPickerQuery } from './tool-test-tenants';
import { ToolTestContactPicker } from './ToolTestContactPicker';
import { MAX_HISTORY, ToolTestHistory, type ToolTestRunRecord } from './ToolTestHistory';
import { ToolTestRunView } from './ToolTestRunView';
import styles from './ToolTestPanel.module.css';

const hostFromBaseUrl = (baseUrl: string): string =>
  URL.canParse(baseUrl) ? new URL(baseUrl).host : baseUrl;

/** Shape-only example of a Reach Voice call UUID, shown as the sample field's placeholder. */
export const SAMPLE_VOICE_CALL_ID = '0198f0f2-7b65-7a6f-8c43-1f67d89c2e10';

interface HttpToolTestPanelProps {
  tool: HttpToolDefinition;
  onSourceResponse?: (response: unknown) => void;
}

export const HttpToolTestPanel = ({ tool, onSourceResponse }: HttpToolTestPanelProps) => {
  const exampleArgs = useMemo(
    () => JSON.stringify(buildArgsExample(tool.request_schema, tool.request_mapping), null, 2),
    [tool.request_schema, tool.request_mapping],
  );

  const [businessId, setBusinessId] = useState('');
  const [selectedContact, setSelectedContact] = useState<PartnerUser | null>(null);
  const [participantPhone, setParticipantPhone] = useState('');
  const [voiceCallId, setVoiceCallId] = useState('');
  const [argsText, setArgsText] = useState(exampleArgs);
  const [argsValid, setArgsValid] = useState(true);
  const [runs, setRuns] = useState<ToolTestRunRecord[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const nextRunId = useRef(0);

  const { data: tenants } = useQuery(tenantPickerQuery);
  const tenantList = tenants?.data?.results ?? [];
  const selectedTenant = tenantList.find(tenant => tenant.id === businessId);

  const claimEnforced = Boolean(
    tool.claim_enforcement && Object.keys(tool.claim_enforcement).length > 0,
  );
  const toolTemplates = { baseUrl: tool.base_url, requestMapping: tool.request_mapping };
  const phoneIsReachSourced = referencesContextSource(toolTemplates, 'participantPhone');
  const voiceCallIdIsReachSourced = referencesContextSource(toolTemplates, 'voiceCallId');
  // Resolving the contact ID needs a partner_user, same as claim enforcement does.
  const externalIdIsReachSourced = referencesContextSource(toolTemplates, 'contactExternalId');
  const contactRequired = claimEnforced || externalIdIsReachSourced;
  const needsContact = contactRequired && !selectedContact;

  const testMutation = useMutation({
    mutationFn: () => {
      const args = argsValid ? (JSON.parse(argsText) as Record<string, unknown>) : {};
      return partnerApi.testTool(tool.id, {
        businessId,
        partnerUserId: selectedContact?.id,
        participantPhone: participantPhone.trim() ? participantPhone.trim() : undefined,
        voiceCallId: voiceCallId.trim() ? voiceCallId.trim() : undefined,
        scope: normalizeToolScope(tool.required_scope),
        args,
      });
    },
    onSuccess: run => {
      nextRunId.current += 1;
      const record: ToolTestRunRecord = { ...run, id: nextRunId.current, at: new Date() };
      setRuns(previous => [record, ...previous].slice(0, MAX_HISTORY));
      setSelectedRunId(record.id);
      const { sourceResponse } = run;
      const runSucceeded = 'status' in run.result && run.result.status === 'success';
      if (runSucceeded && sourceResponse?.bodyFormat === ToolTestSourceBodyFormatEnum.JSON) {
        onSourceResponse?.(sourceResponse.body);
      }
    },
  });

  const runDisabled = !businessId || !argsValid || needsContact || testMutation.isPending;
  const selectedRun = runs.find(run => run.id === selectedRunId);

  const contactSearchLabel = `Contact ${contactRequired ? '(required)' : '(optional)'}`;
  const contactMissingHint = claimEnforced
    ? 'This tool enforces claims — pick the contact to run as.'
    : `This tool sends the contact ID from Reach (${CONTACT_EXTERNAL_ID_TOKEN}) — pick a contact so there's an ID to send.`;

  const phoneSampleLabel = `${CONTEXT_SOURCES.participantPhone.label} from Reach (simulate)`;
  const phoneSampleHint = `This tool sends the contact phone from Reach (${PARTICIPANT_PHONE_TOKEN}) — without one here, the run will fail validation.`;
  const voiceCallIdSampleLabel = `${CONTEXT_SOURCES.voiceCallId.label} (simulate)`;
  const voiceCallIdSampleHint = `This tool sends the Voice call ID from Reach (${VOICE_CALL_ID_TOKEN}) — without one here, the run will fail validation.`;

  return (
    <fieldset className={styles.panel}>
      <legend className={styles.legend}>Test runner</legend>
      <p className={styles.description}>{RESPONSE_MAPPING_COPY.testRunnerDescription}</p>
      <div className={styles.fields}>
        <div className={styles.field}>
          <label htmlFor="tool-test-tenant" className={styles.fieldLabel}>
            Tenant
          </label>
          <select
            id="tool-test-tenant"
            value={businessId}
            onChange={e => {
              setBusinessId(e.target.value);
              setSelectedContact(null);
            }}
            className={styles.selectFull}>
            <option value="">Pick a tenant…</option>
            {tenantList.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.id.slice(0, 8)}…)
              </option>
            ))}
          </select>
          {selectedTenant &&
            (selectedTenant.externalId ? (
              <span className={styles.externalIdRow}>
                External ID: <code>{selectedTenant.externalId}</code>
                <CopyButton value={selectedTenant.externalId} />
              </span>
            ) : (
              <span className={styles.hint}>
                This tenant has no external ID — tools that use {TENANT_EXTERNAL_ID_TOKEN} will fail
                validation.
              </span>
            ))}
        </div>

        <ToolTestContactPicker
          key={businessId}
          businessId={businessId}
          searchLabel={contactSearchLabel}
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
          hint={needsContact && <span className={styles.hint}>{contactMissingHint}</span>}
        />

        {phoneIsReachSourced && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{phoneSampleLabel}</span>
            <input
              type="text"
              value={participantPhone}
              onChange={e => setParticipantPhone(e.target.value)}
              className={styles.monoInput}
              placeholder="+15555550123"
            />
            <span className={styles.hint}>{phoneSampleHint}</span>
          </label>
        )}

        {voiceCallIdIsReachSourced && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{voiceCallIdSampleLabel}</span>
            <input
              type="text"
              value={voiceCallId}
              onChange={e => setVoiceCallId(e.target.value)}
              className={styles.monoInput}
              placeholder={SAMPLE_VOICE_CALL_ID}
            />
            <span className={styles.hint}>{voiceCallIdSampleHint}</span>
          </label>
        )}

        <div className={styles.field}>
          <div className={styles.argsHeader}>
            <span className={styles.fieldLabel}>Args (JSON)</span>
            <button
              type="button"
              className={styles.resetButton}
              onClick={() => setArgsText(exampleArgs)}>
              Reset to example
            </button>
          </div>
          <div className={styles.fieldControl}>
            <MonacoJsonEditor
              value={argsText}
              onChange={setArgsText}
              onValidationChange={setArgsValid}
              height="120px"
            />
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => testMutation.mutate()}
            disabled={runDisabled}
            className={styles.primaryButton}>
            <Play />
            {testMutation.isPending ? 'Running…' : 'Run test'}
          </button>
          {testMutation.error && <span className={styles.errorText}>{testMutation.error.message}</span>}
        </div>

        {testMutation.isPending && (
          <div className={styles.pending}>
            <Loader2 className={styles.spinner} />
            Calling {tool.http_method.toUpperCase()} {hostFromBaseUrl(tool.base_url)}…
          </div>
        )}

        {!testMutation.isPending && selectedRun && <ToolTestRunView run={selectedRun} />}

        <ToolTestHistory runs={runs} selectedId={selectedRunId} onSelect={setSelectedRunId} />
      </div>
    </fieldset>
  );
};
